/**
 * Full-screen mobile list picker for a bulk selection: shows the signed-in user's shopping lists,
 * adds the selected products to the one tapped, or creates a list and adds them to it. Rendered
 * below the `lg` breakpoint by the catalog (`components/Catalog/CatalogItemsMobile.tsx`), search
 * (`components/Search/Search.tsx`), the cart header
 * (`components/ShoppingCart/ShoppingCartHeader/ShoppingCartHeader.tsx`) and list details
 * (`components/ShoppingListDetails/Helpers/ShoppingLIstMobileContent.tsx`). The desktop and
 * single-item counterpart is `AddToShoppingList.tsx`.
 *
 * The caller owns the open state (`mobileSliderOpen`) and the success message; this component
 * slides in and out on that flag, locks body scrolling while open and puts the saved inline
 * style back on close.
 *
 * Unlike `AddToShoppingList.tsx`, it does not validate quantities or cuts, has no guest handling
 * (every call in `components/Helper/shoppingListHelper.ts` does nothing for a guest), and does not
 * disable Create List while the create call runs. The lists are fetched on mount and when
 * `isLoggedIn` changes.
 */
import React, { createRef, RefObject, useEffect, useState } from "react";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { SliderMenu } from "@components/Helper/commonStyles";
import { useTheme } from "@ryerson/frontend.theme";
import { Icon } from "@ryerson/frontend.assets";
import { css } from "@emotion/react";
import rgba from "polished/lib/color/rgba";
import { Typography } from "@ryerson/frontend.typography";
import styled from "@emotion/styled";
import { Button } from "@ryerson/frontend.button";
import { AddToShoppingListContent } from "./AddToShoppingListContent";
import { useApplication } from "@ryerson/frontend.application";
import { ShoppingList } from "./AddToShoppingList";
import { useWindowSize } from "@ryerson/frontend.common";
import { Input } from "@ryerson/frontend.form-controls";
import {
	addProductsToShoppingList,
	AjaxCreateShoppingListResponse,
	AjaxGetShoppingListResponse,
	createShoppingList,
	getShoppingList,
	ShoppingListItemsRequest,
	ShoppingListProduct,
} from "@components/Helper/shoppingListHelper";
import { CatalogSuccessMessageContent } from "@components/Catalog/CatalogHelper";
import { getZipCode } from "@components/AccountSettings/Helpers/zipCode";
import { useEcommerce } from "@ryerson/frontend.e-commerce";
import { TranslatedText, useTranslation } from "@components/Shared/TranslationContext";

interface BulkAddToShoppingListProps {
	/** Accepted but not read. */
	testingId: string;
	/** The selected lines, in the Shopping API shape. */
	products: ShoppingListProduct[];
	mobileSliderOpen: boolean;
	/** Called with `false` by Back, close, and after a successful add. */
	setMobileSliderOpen: (value: boolean) => void;
	/** Called with `true` after a successful add. */
	setShowSuccessMessage: (value: boolean) => void;
	/** Called with the add-items response `data` after a successful add. */
	onAddToShoppingList: (addedShoppingListResponse: any) => void;
	/** Set to `CatalogSuccessMessageContent.ShoppingListSuccess` after a successful add. */
	setSuccessMessageContent: React.Dispatch<React.SetStateAction<string>>;
}

const closeFiltersPane = (props: any) => {
	const { theme } = props;
	return css`
		height: 50px;
		width: 50px;
		border-left: 1px solid ${rgba(theme.theme.colors.primary.white, 0.1)};
	`;
};

const GlobalNavigation = styled.div`
	height: 50px;
	width: 100%;
	top: 0;
	${(props: any) => {
		const { theme } = props;
		return css`
			border-bottom: 1px solid ${theme.theme.colors.primary.lightGray};
			background-color: ${theme.theme.colors.primary.darkGray};
		`;
	}}
`;

const Divider = styled.div`
	margin: 20px 0;
	opacity: 0.1;
	${(props: any) => {
		const { color } = props;
		return `
			border-top: 1px solid ${color};
		`;
	}};
`;

const ShoppingListMenu = styled(Flex)`
	height: 100%;
	${(props: any) => {
		return `
			background-color: ${props.background};
			padding: 0 20px;
			border-radius: 2px;
			box-shadow: 0px 100px 80px rgba(0, 0, 0, 0.07), 0px 0px 17.869px rgba(0, 0, 0, 0.0417275),
				0px 6.6501px 5.32008px rgba(0, 0, 0, 0.0282725);
			@media only screen and (min-width: ${props.breakpoint}) {
				width: 100%;
				min-width: 230px;
			}
			@media only screen and (max-width: calc( ${props.breakpoint} - 1px )) {
				margin-right: 0px;
			}
		`;
	}};
`;

const ShoppingListMenuItem = styled(FlexItem)`
	margin: 20px 0 5px 0;
	width: 100%;
`;

const ShoppingListsContainer = styled.div`
	height: 85%;
	overflow-y: auto;
`;

const ShoppingListsItem = styled(Flex)`
	margin: 10px 0;
	cursor: pointer;
	${(props: any) => {
		return `
			&:hover span {
				color: ${props.color};
			}
		`;
	}};
`;

const ModalContentMobile = styled(Flex)`
	width: 100vw;
	overflow: auto;
	bottom: 0;

	${(props: any) => {
		const { backgroundColor } = props;
		return css`
			background: ${backgroundColor};
		`;
	}};
`;

const ModalContentMobileInner = styled.div`
	padding: 20px;
`;

/**
 * The slider has two views: the list picker and the create-list form. Back returns from the
 * form to the picker, or closes the slider from the picker.
 */
const BulkAddToShoppingListMobile: React.FC<BulkAddToShoppingListProps> = ({
	testingId,
	products,
	mobileSliderOpen,
	setMobileSliderOpen,
	setShowSuccessMessage,
	onAddToShoppingList,
	setSuccessMessageContent,
}) => {
	const theme = useTheme();
	const { white, gray, darkGray, link } = theme.theme.colors.primary;

	const {
		axiosInstance,
		localization: { language },
		user: { isLoggedIn },
		isNative,
		zipCode,
	} = useApplication();
	const { translations } = useTranslation();

	const [domNodeRefs] = useState<any>({});
	const [bodyStyles, setBodyStyles] = useState<string>("");
	const [hasMounted, setHasMounted] = React.useState<boolean>(false);
	const [isShoppingListExpanded, setIsShoppingListExpanded] = useState<boolean>(false);
	const [isNewShoppingListModalOpen, setIsNewShoppingListModalOpen] = useState<boolean>(false);
	const [newListName, setNewListName] = useState<string>("");
	const [shoppingLists, setShoppingLists] = useState<ShoppingList[]>([]);

	const windowSize = useWindowSize();

	const content = AddToShoppingListContent[language] || AddToShoppingListContent.en;
	const { customerPickup, selectedShipToAddress, salesPlant } = useEcommerce();

	const requestBody: ShoppingListItemsRequest = {
		zipCode: getZipCode(isLoggedIn, zipCode, selectedShipToAddress, customerPickup, salesPlant),
		items: products,
	};

	const creatDomNodeRef = (fieldName: string): RefObject<any> => {
		domNodeRefs[fieldName] = createRef();
		return domNodeRefs[fieldName];
	};

	const toggleMobileSlider = () => {
		setMobileSliderOpen(false);
	};

	// On mount while closed, this sets the body's inline style to "" (bodyStyles starts empty).
	useEffect(() => {
		setHasMounted(true);

		const body = document?.querySelector("body");

		if (mobileSliderOpen) {
			(window as any).scrollTo(0, 0);
			setBodyStyles(body?.getAttribute("style") || "");
			setTimeout(() => {
				body?.setAttribute("style", "overflow: hidden;");
			}, 100);
		} else {
			body && body.setAttribute("style", bodyStyles);
		}
	}, []);

	useEffect(() => {
		if (hasMounted) {
			const sliderEl = domNodeRefs["mobile-shopping-list-modal"].current;

			sliderEl?.classList?.toggle("slide-open");
			sliderEl?.classList?.toggle("slide-close");

			const body = document?.querySelector("body");

			if (mobileSliderOpen) {
				setIsNewShoppingListModalOpen(false);
				(window as any).scrollTo(0, 0);
				setBodyStyles(body?.getAttribute("style") || "");
				setTimeout(() => {
					body?.setAttribute("style", "overflow: hidden;");
				}, 100);
			} else {
				body && body.setAttribute("style", bodyStyles);
			}
		}
	}, [mobileSliderOpen]);

	// isShoppingListExpanded starts false and is only ever set to false, so in practice this
	// fetches on mount and when isLoggedIn changes, not on each open.
	useEffect(() => {
		if (isLoggedIn) {
			getShoppingList(isLoggedIn, axiosInstance, handleOnSuccessGetShoppingList);
		}
	}, [isShoppingListExpanded, isLoggedIn]);

	useEffect(() => {
		setHasMounted(true);
		setIsNewShoppingListModalOpen(false);
	}, []);

	const addNewShoppingList = () => {
		if (isLoggedIn) {
			setIsNewShoppingListModalOpen(true);
		}
	};

	const createNewShoppingList = () => {
		if (isLoggedIn && newListName.length > 0) {
			const body = {
				name: newListName,
			};
			createShoppingList(isLoggedIn, axiosInstance, body, handleOnSuccessCreateShoppingList);
		}
	};

	const handleOnSuccessAddToList = (response: any) => {
		const responseData = response?.data;
		if (responseData) {
			onAddToShoppingList(responseData);
			setSuccessMessageContent(CatalogSuccessMessageContent.ShoppingListSuccess);
			setShowSuccessMessage(true);
			setMobileSliderOpen(false);
		}
	};

	// Appends the new list locally (no re-fetch), then adds the selected products to it.
	const handleOnSuccessCreateShoppingList = (response: AjaxCreateShoppingListResponse) => {
		const responseData = response?.data;
		if (responseData) {
			setShoppingLists([
				...shoppingLists,
				{
					shoppingListId: responseData.shoppingListId,
					name: responseData.name,
				},
			]);
			setNewListName("");
			addProductsToShoppingList(
				requestBody,
				responseData.shoppingListId,
				isLoggedIn,
				axiosInstance,
				handleOnSuccessAddToList
			);
		}
	};

	const handleOnSuccessGetShoppingList = (response: AjaxGetShoppingListResponse) => {
		const responseData = response?.data;
		if (responseData) {
			setShoppingLists([...responseData]);
		}
	};

	const handleListNameChange = (event: any) => {
		setNewListName(event.target.value);
	};

	return (
		<>
			<SliderMenu
				{...{ theme: theme.theme }}
				ref={creatDomNodeRef(`mobile-shopping-list-modal`)}
				className="mobile-shopping-list-modal slide-close"
				css={{ top: isNative ? "calc(env(safe-area-inset-top) + 60px)" : 100 }}
			>
				<Flex css={{ height: "100%" }} direction="column">
					<Flex css={{ height: "100%", overflowY: "scroll" }} direction="column">
						<GlobalNavigation theme={theme}>
							<Flex alignItems="center">
								<FlexItem grow={1}>
									<Flex
										justifyContent="flex-start"
										onClick={() => {
											isNewShoppingListModalOpen
												? setIsNewShoppingListModalOpen(false)
												: toggleMobileSlider();
										}}
									>
										<FlexItem alignSelf="center" css={{ marginLeft: 20 }}>
											<Icon
												icon="chevron-left"
												size="md"
												color={white}
												circled={true}
												css={{ borderColor: gray }}
											/>
										</FlexItem>
										<FlexItem
											css={css`
												padding-left: 10px;
											`}
										>
											<Typography
												variant="p"
												color={theme.theme.colors.tertiary.label}
												size="md"
											>
												Back
											</Typography>
										</FlexItem>
									</Flex>
								</FlexItem>
								<FlexItem>
									<Flex
										style={closeFiltersPane({ theme: theme })}
										justifyContent="center"
										alignItems="center"
									>
										<Icon
											icon="close"
											size="sm"
											onClick={toggleMobileSlider}
											color={white}
										/>
									</Flex>
								</FlexItem>
							</Flex>
						</GlobalNavigation>
						{!isNewShoppingListModalOpen ? (
							<ShoppingListMenu
								{...{
									breakpoint: theme.theme.breakpoints.lg,
									background: theme.theme.colors.primary.background,
								}}
							>
								<ShoppingListMenuItem>
									<Button
										size={"sm"}
										fullwidth={true}
										shape="rounded"
										foreground={"light"}
										label={content.newShoppingList}
										onClick={addNewShoppingList}
										background={darkGray}
										dataTestId="shopping-list-details-mobile-new-list-create-button"
									/>
									<Divider color={gray} />
									<ShoppingListsContainer>
										{shoppingLists.map(
											(shoppingList: ShoppingList, index: number) => {
												return (
													<ShoppingListsItem
														justifyContent="space-between"
														alignItems="center"
														key={index}
														{...{ color: link }}
														onClick={() => {
															addProductsToShoppingList(
																requestBody,
																shoppingList.shoppingListId,
																isLoggedIn,
																axiosInstance,
																handleOnSuccessAddToList
															);
															setIsShoppingListExpanded(false);
														}}
													>
														<FlexItem>
															<Typography size="sm">
																{shoppingList.name}
															</Typography>
														</FlexItem>
													</ShoppingListsItem>
												);
											}
										)}
									</ShoppingListsContainer>
								</ShoppingListMenuItem>
							</ShoppingListMenu>
						) : (
							<ModalContentMobile
								direction="column"
								css={{ height: windowSize[1] - 100, overflowY: "scroll" }}
							>
								<ModalContentMobileInner>
									<Typography variant="h2">{content.createNewList}</Typography>
									<Input
										label={content.listName}
										value={newListName}
										required={true}
										onChange={handleListNameChange}
										type="secondary"
										language={language}
										css={{ marginTop: "15px" }}
										dataTestId="shopping-list-details-mobile-create-new-list-input"
									/>
									<Typography variant="p">
										<TranslatedText
											text={translations.newShoppingListSubText}
										/>
									</Typography>
									<Button
										label={content.createList}
										fullwidth={true}
										onClick={createNewShoppingList}
										disabled={newListName.length < 1}
										dataTestId="shopping-list-details-mobile-create-new-list-button"
									/>
								</ModalContentMobileInner>
							</ModalContentMobile>
						)}
					</Flex>
				</Flex>
			</SliderMenu>
		</>
	);
};

export default BulkAddToShoppingListMobile;
